import {
  Alert,
  Box,
  Icon,
  Image,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from "@outschool/backpack";
import {
  Activity,
  ActivityAttributeKey,
  ActivityCurriculum,
  ActivityStandard,
  isActivityCurriculum,
  isActivityStandard,
} from "@outschool/business-rules";
import {
  ClassDetailsNoSectionsQuery,
  EnglishProficiencyLevel,
} from "@outschool/gql-frontend-generated";
import {
  IconDefinition,
  faBaby,
  faGraduationCap,
  faQuestionCircle,
  faSeedlingRegular,
} from "@outschool/icons";
import {
  I18nLocale,
  TFunction,
  useLocale,
  useTranslation,
} from "@outschool/localization";
import { Space } from "@outschool/ui-legacy-component-library";
import { head, isEmpty, last, upperFirst } from "lodash";
import React from "react";

import LanguageOfInstructionImg from "../../images/language_of_instruction_img.svg";
import useActivityString from "../translations/useActivityString";
import { YellowBlobIcon } from "./IconWithBackground";

function getActivityAttributeValue(
  attributes: Activity["attributes"],
  key: (typeof ActivityAttributeKey)[keyof typeof ActivityAttributeKey]
) {
  return attributes?.find(attr => attr.key === key)?.value;
}

export function getCurriculumValue(attributes: Activity["attributes"]) {
  const value = getActivityAttributeValue(
    attributes,
    ActivityAttributeKey.curriculum
  );

  return value &&
    isActivityCurriculum(value) &&
    value !== ActivityCurriculum.Other
    ? value
    : undefined;
}

export function getStandardValue(attributes: Activity["attributes"]) {
  const value = getActivityAttributeValue(
    attributes,
    ActivityAttributeKey.standard
  );

  return value && isActivityStandard(value) && value !== ActivityStandard.Other
    ? value
    : undefined;
}

type Activity = ClassDetailsNoSectionsQuery["activity"];

type ActivityAttributeProps = {
  activity: Activity;
  t: TFunction;
};

type Props = {
  activity: Activity;
  sx?: SxProps;
};

export default function ActivityAttributes({ activity, sx }: Props) {
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\ActivityAttributes"
  );
  const hasGradeLevel = !!getActivityAttributeValue(
    activity.attributes,
    ActivityAttributeKey.gradeLevelMin
  );

  const hasProficiency = !!getActivityAttributeValue(
    activity.attributes,
    ActivityAttributeKey.proficiencyLevel
  );

  const locale = useLocale();

  const isNonUSLocale = locale !== I18nLocale.En;

  const hasCurriculum = !!getCurriculumValue(activity.attributes);

  const hasStandard = !!getStandardValue(activity.attributes);

  const isToddlerClass = Activity.isToddlerClass(activity);

  if (
    !hasGradeLevel &&
    !hasProficiency &&
    !hasCurriculum &&
    !hasStandard &&
    !isToddlerClass
  ) {
    return <Space y="medium" />;
  }
  return (
    <>
      <Box
        sx={[
          (theme: Theme) => ({
            display: "block",
            gap: 0,
            paddingY: "1rem",
            [theme.breakpoints.up("md")]: {
              display: "grid",
              gap: "2.4rem 4rem",
              gridTemplateColumns: "1fr 1fr",
              paddingBottom: "2rem",
              paddingTop: ".7rem",
            },
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {isNonUSLocale && (
          <ActivityEnglishProficiencyLevels activity={activity} t={t} />
        )}
        {hasGradeLevel && <ActivityGradeLevels activity={activity} t={t} />}
        {hasProficiency && (
          <ActivityProficiencyLevel activity={activity} t={t} />
        )}
        {isToddlerClass && (
          <ActivityAttribute
            activityIcon={faBaby}
            activityDescription={t`Together Time (ages 1-2)`}
          />
        )}
      </Box>
      {isToddlerClass && (
        <Alert
          severity="info"
          sx={{ marginBottom: "1em" }}
          title={t`Guardian required in class`}
        >{t`Classes for learners in the 1-2 year age range will require a guardian to be present`}</Alert>
      )}
    </>
  );
}

function ActivityAttribute({
  activityIcon,
  activitySvg,
  activityDescription,
}: {
  activityIcon?: IconDefinition;
  activityDescription: React.ReactNode;
  activitySvg?: string;
}) {
  return (
    <Box
      sx={(theme: Theme) => ({
        paddingY: 6,
        display: "flex",
        alignItems: "center",
        gap: "0.8rem",
        [theme.breakpoints.up("md")]: {
          paddingY: 0,
        },
      })}
    >
      {activityIcon && (
        <YellowBlobIcon
          iconSx={(theme: Theme) => ({
            display: "inline",
            fontSize: 20,
            [theme.breakpoints.up("md")]: {
              display: "block",
              fontSize: 28,
            },
          })}
          icon={activityIcon}
        />
      )}
      {activitySvg && (
        <Box
          component="span"
          sx={(theme: Theme) => ({
            display: "inline",
            verticalAlign: "middle",
            width: 20,
            height: 20,
            [theme.breakpoints.up("md")]: {
              display: "block",
              width: 28,
              height: 28,
            },
          })}
        >
          <Image src={activitySvg} />
        </Box>
      )}
      <Typography
        variant="subtitle1"
        sx={(theme: Theme) => ({
          display: "inline",
          [theme.breakpoints.up("md")]: {
            display: "block",
          },
        })}
      >
        {activityDescription}
      </Typography>
    </Box>
  );
}

function ActivityGradeLevels({ activity, t }: ActivityAttributeProps) {
  const { gradeLevelString } = useActivityString();
  const gradeRange = gradeLevelString(activity, false);
  const description = t("US Grade {{gradeRange}}", {
    gradeRange,
  });
  return (
    <ActivityAttribute
      activityIcon={faGraduationCap}
      activityDescription={description}
    />
  );
}

function ActivityProficiencyLevel({ activity, t }: ActivityAttributeProps) {
  const order = ["beginner", "intermediate", "advanced"];
  const activityProficiencyLevel = activity?.attributes
    ?.filter(attr => attr.key === "proficiency_level")
    .map(attr => attr.value)
    .sort((a, b) => order.indexOf(a) - order.indexOf(b));

  const proficiencyLevel = !isEmpty(activityProficiencyLevel)
    ? activityProficiencyLevel.length > 1
      ? `${upperFirst(
          head(activityProficiencyLevel)?.toLowerCase()
        )} - ${upperFirst(last(activityProficiencyLevel)?.toLowerCase())}`
      : upperFirst(head(activityProficiencyLevel)?.toLowerCase())
    : "";

  const description = t("{{proficiencyLevel}} Level", {
    proficiencyLevel,
  });
  return (
    <ActivityAttribute
      activityIcon={faSeedlingRegular}
      activityDescription={description}
    />
  );
}

const getOptionForLevel = (level: EnglishProficiencyLevel | null) => {
  return (
    englishProficiencyLevelOptions.find(option => option.level === level) ||
    null
  );
};

function ActivityEnglishProficiencyLevels({
  activity,
  t,
}: ActivityAttributeProps) {
  const option = getOptionForLevel(activity.english_proficiency_level);
  let description;
  let tooltip = "";

  if (option) {
    if (option.value !== 1) {
      description = t("English Level - {{englishProficiencyLevel}}", {
        englishProficiencyLevel: option.label,
      });
      tooltip = t("{{description}}", {
        description: option.description,
      });
    } else {
      description = t("English Level - Unknown");
    }
  }

  return (
    <ActivityAttribute
      activitySvg={LanguageOfInstructionImg}
      activityDescription={
        <>
          {description}
          {tooltip && (
            <Tooltip placement="top" title={tooltip}>
              <Icon
                icon={faQuestionCircle}
                sx={{
                  color: "primary.800",
                  marginLeft: 4,
                }}
              />
            </Tooltip>
          )}
        </>
      }
    />
  );
}

export const englishProficiencyLevelOptions = [
  {
    value: 1,
    level: null,
    label: "N/A",
    description: "Don't specify English proficiency level",
  },
  {
    value: 2,
    level: EnglishProficiencyLevel.A0,
    label: "A0",
    description: "Any learner who just started learning English.",
  },
  {
    value: 3,
    level: EnglishProficiencyLevel.A05,
    label: "A0.5",
    description:
      "Any learner who is familiar with letter sounds and can respond to basic questions with 1-2 word answers.",
  },
  {
    value: 4,
    level: EnglishProficiencyLevel.PreA1,
    label: "Pre-A1",
    description:
      "Any learner who has a basic understanding of English education topics such as colors, weather, and numbers.",
  },
  {
    value: 5,
    level: EnglishProficiencyLevel.A1,
    label: "A1",
    description:
      "Any learner who can engage in simple conversations about everyday subjects.",
  },
  {
    value: 6,
    level: EnglishProficiencyLevel.A2,
    label: "A2",
    description:
      "Any learner who can share their experiences and ask questions.",
  },
  {
    value: 7,
    level: EnglishProficiencyLevel.B1,
    label: "B1",
    description:
      "Any learner who can express their thoughts on common subjects with explanations.",
  },
  {
    value: 8,
    level: EnglishProficiencyLevel.B2,
    label: "B2+",
    description:
      "Any learner who can actively engage in discussion on current issues.",
  },
];
